import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useOutsideAlerter } from "../../../../utils/outsideAlerter";
import { SearchContext } from "../../../Search/SearchContext";
import {
  DRAG_HANDLE_COLORS,
  DragHandle,
  DragIndicator,
  TrayContainer,
  TrayContent,
  TrayScrollContainer,
} from "./__styles__/MobileBottomTray";

interface MobileBottomTrayProps {
  children: ReactNode;
}

export const MobileBottomTray = ({ children }: MobileBottomTrayProps) => {
  const { searchQueryState } = useContext(SearchContext);
  const searchPanelOpen = searchQueryState.searchPanelOpen;

  const [trayExpanded, setTrayExpanded] = useState<boolean>(false);
  const [isScrolledPastHeader, setIsScrolledPastHeader] = useState(false);
  const isTransitioning = useRef(false);
  const pointerStartY = useRef<number | null>(null);
  const headerHeightRef = useRef<number>(0);

  const trayRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const isFromDragHandle = useCallback(
    (target: EventTarget | null) =>
      Boolean(
        trayRef.current?.contains(target as Node) &&
        !scrollContainerRef.current?.contains(target as Node),
      ),
    [],
  );

  const isCloseButton = useCallback(
    (target: EventTarget | null) =>
      Boolean((target as HTMLElement)?.closest("[data-panel-close]")),
    [],
  );

  const collapse = useCallback(() => {
    setTrayExpanded(false);
  }, []);

  const onOutsideInteraction = useCallback(() => {
    if (trayExpanded) {
      collapse();
    }
  }, [trayExpanded, collapse]);

  useOutsideAlerter({
    ref: trayRef,
    onOutsideInteraction,
  });

  const expandIfCollapsed = useCallback(() => {
    if (!trayExpanded && !isTransitioning.current) {
      isTransitioning.current = true;
      setTrayExpanded(true);
      setTimeout(() => {
        isTransitioning.current = false;
      }, 300);
    }
  }, [trayExpanded]);

  const collapseIfExpanded = useCallback(() => {
    if (trayExpanded && !isTransitioning.current) {
      isTransitioning.current = true;
      collapse();
      setTimeout(() => {
        isTransitioning.current = false;
      }, 300);
    }
  }, [trayExpanded, collapse]);

  const handleDragStart = useCallback(
    (clientY: number, event: Event) => {
      pointerStartY.current = clientY;
      if (!trayExpanded) {
        event.preventDefault();
      }
    },
    [trayExpanded],
  );

  const handleDragMove = useCallback(
    (
      clientY: number,
      { fromDragHandle }: { fromDragHandle: boolean } = {
        fromDragHandle: false,
      },
    ) => {
      if (isTransitioning.current || pointerStartY.current === null) {
        return;
      }

      const deltaY = pointerStartY.current - clientY;
      const isAtTop =
        scrollContainerRef.current && scrollContainerRef.current.scrollTop <= 0;

      if (!trayExpanded && deltaY > 10) {
        expandIfCollapsed();
        pointerStartY.current = null;
      } else if (trayExpanded && (isAtTop || fromDragHandle) && deltaY < -10) {
        collapseIfExpanded();
        pointerStartY.current = null;
      }
    },
    [trayExpanded, expandIfCollapsed, collapseIfExpanded],
  );

  const handleDragEnd = useCallback(
    (clientY: number, target: EventTarget | null) => {
      if (!trayExpanded && pointerStartY.current !== null) {
        const deltaY = Math.abs(pointerStartY.current - clientY);
        if (deltaY < 10 && !isCloseButton(target)) {
          expandIfCollapsed();
        }
      }
      pointerStartY.current = null;
    },
    [trayExpanded, expandIfCollapsed, isCloseButton],
  );

  const handleTouchStart = useCallback(
    (event: TouchEvent) => {
      handleDragStart(event.touches[0]?.clientY ?? 0, event);
    },
    [handleDragStart],
  );

  const handleTouchMove = useCallback(
    (event: TouchEvent) => {
      const fromDragHandle = isFromDragHandle(event.target);
      handleDragMove(event.touches[0]?.clientY ?? 0, { fromDragHandle });
    },
    [handleDragMove, isFromDragHandle],
  );

  const handleTouchEnd = useCallback(
    (event: TouchEvent) => {
      handleDragEnd(event.changedTouches[0]?.clientY ?? 0, event.target);
    },
    [handleDragEnd],
  );

  const handleMouseDown = useCallback(
    (event: MouseEvent) => {
      handleDragStart(event.clientY, event);
    },
    [handleDragStart],
  );

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      const fromDragHandle = isFromDragHandle(event.target);
      handleDragMove(event.clientY, { fromDragHandle });
    },
    [handleDragMove, isFromDragHandle],
  );

  const handleMouseUp = useCallback(
    (event: MouseEvent) => {
      handleDragEnd(event.clientY, event.target);
    },
    [handleDragEnd],
  );

  const handleWheel = useCallback(
    (event: WheelEvent) => {
      if (isTransitioning.current) {
        event.preventDefault();
        return;
      }

      const isAtTop =
        scrollContainerRef.current && scrollContainerRef.current.scrollTop <= 0;

      if (!trayExpanded && event.deltaY > 20) {
        event.preventDefault();
        expandIfCollapsed();
      } else if (trayExpanded && isAtTop && event.deltaY < -20) {
        event.preventDefault();
        collapseIfExpanded();
      }
    },
    [trayExpanded, expandIfCollapsed, collapseIfExpanded],
  );

  const updateHeaderHeight = useCallback(() => {
    const header = scrollContainerRef.current?.querySelector(
      "[data-panel-header]",
    );
    if (header) {
      headerHeightRef.current = header.getBoundingClientRect().height;
    }
  }, []);

  const handleScroll = useCallback(() => {
    updateHeaderHeight();
    const scrollTop = scrollContainerRef.current?.scrollTop ?? 0;
    setIsScrolledPastHeader(
      headerHeightRef.current > 0 && scrollTop >= headerHeightRef.current,
    );
  }, [updateHeaderHeight]);
  useEffect(() => {
    const tray = trayRef.current;
    if (!tray) return;

    tray.addEventListener("touchstart", handleTouchStart, { passive: false });
    tray.addEventListener("touchmove", handleTouchMove, { passive: false });
    tray.addEventListener("touchend", handleTouchEnd);
    tray.addEventListener("mousedown", handleMouseDown);
    tray.addEventListener("mousemove", handleMouseMove);
    tray.addEventListener("mouseup", handleMouseUp);
    return () => {
      tray.removeEventListener("touchstart", handleTouchStart);
      tray.removeEventListener("touchmove", handleTouchMove);
      tray.removeEventListener("touchend", handleTouchEnd);
      tray.removeEventListener("mousedown", handleMouseDown);
      tray.removeEventListener("mousemove", handleMouseMove);
      tray.removeEventListener("mouseup", handleMouseUp);
    };
  }, [
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  ]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    scrollContainer.addEventListener("wheel", handleWheel, { passive: false });
    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      scrollContainer.removeEventListener("wheel", handleWheel);
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, [handleWheel, handleScroll]);

  useEffect(() => {
    updateHeaderHeight();
  }, [children, updateHeaderHeight]);

  return (
    <TrayContainer style={{ display: searchPanelOpen ? "none" : undefined }}>
      <TrayContent ref={trayRef} trayExpanded={trayExpanded}>
        <DragHandle
          data-testid="bottom-tray-drag-handle"
          style={{
            backgroundColor:
              isScrolledPastHeader ?
                DRAG_HANDLE_COLORS.light.handle
              : DRAG_HANDLE_COLORS.dark.handle,
          }}
        >
          <DragIndicator
            style={{
              backgroundColor:
                isScrolledPastHeader ?
                  DRAG_HANDLE_COLORS.light.indicator
                : DRAG_HANDLE_COLORS.dark.indicator,
            }}
          />
        </DragHandle>
        <TrayScrollContainer
          ref={scrollContainerRef}
          data-testid="bottom-tray-container"
          style={{
            overflowY: trayExpanded ? "auto" : "hidden",
          }}
        >
          {children}
        </TrayScrollContainer>
      </TrayContent>
    </TrayContainer>
  );
};
